import React, { useEffect, useState, useCallback } from "react";
import { FileText, Download, Trash2, FolderOpen, Link2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUploader from "@/components/MediaUploader";

const ICON = { pdf: "PDF", doc: "DOC", docx: "DOC", xls: "XLS", xlsx: "XLS", jpg: "IMG", jpeg: "IMG", png: "IMG", webp: "IMG", dwg: "DWG", dxf: "DXF" };
const LABEL = { building: "Gebäude", floor: "Geschoss", plan: "Bauplan", position: "Position", ticket: "Ticket", defect: "Mangel" };

export function downloadFile(f) {
  return api.get(`/files/${f.id}/download`, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click(); URL.revokeObjectURL(url);
  });
}

function DocumentLinker({ link, setLink, opts }) {
  const targets = { building: opts?.buildings, floor: opts?.floors, plan: opts?.plans, position: opts?.positions, ticket: opts?.tickets, defect: opts?.defects }[link.type] || [];
  const nameOf = (o) => o.name || o.number || o.ticket_number || o.title || o.bezeichnung;
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <div><Label className="text-xs">Verknüpfen mit</Label>
        <Select value={link.type} onValueChange={(v) => setLink({ type: v, id: "" })}><SelectTrigger data-testid="doc-link-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="project">Projekt</SelectItem>{Object.entries(LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
      {link.type !== "project" && (
        <div><Label className="text-xs">{LABEL[link.type]} wählen</Label>
          <Select value={link.id || "none"} onValueChange={(v) => setLink({ ...link, id: v === "none" ? "" : v })}><SelectTrigger data-testid="doc-link-target"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent><SelectItem value="none">—</SelectItem>{targets.map((o) => <SelectItem key={o.id} value={o.id}>{nameOf(o)}{o.title && o.number ? ` · ${o.title}` : ""}</SelectItem>)}</SelectContent></Select></div>
      )}
    </div>
  );
}

function DocumentRow({ f, onDelete }) {
  const ext = (f.original_filename || "").split(".").pop().toLowerCase();
  return (
    <div className="flex items-center gap-3 border border-slate-200 rounded-xl p-3 bg-white" data-testid={`doc-${f.id}`}>
      <div className="h-10 w-10 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center text-[10px] font-bold">{ICON[ext] || <FileText className="h-4 w-4" />}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate">{f.original_filename}</div>
        <div className="text-xs text-slate-500 flex gap-2 flex-wrap items-center">
          <span>{(f.created_at || "").slice(0, 10)} · {f.uploaded_by} · {Math.round((f.size || 0) / 1024)} KB</span>
          {f.description && <span>· {f.description}</span>}
          {(f.links || []).map((l) => <span key={l.type} className="inline-flex items-center gap-1 bg-slate-100 rounded px-1.5"><Link2 className="h-3 w-3" />{LABEL[l.type]}: {l.label}</span>)}
        </div>
      </div>
      <button onClick={() => downloadFile(f)} className="text-slate-400 hover:text-primary" data-testid={`download-doc-${f.id}`}><Download className="h-4 w-4" /></button>
      <button onClick={() => onDelete(f.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-doc-${f.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export default function DocumentsPanel({ projectId }) {
  const [docs, setDocs] = useState([]);
  const [opts, setOpts] = useState(null);
  const [link, setLink] = useState({ type: "project", id: "" });
  const load = useCallback(() => api.get(`/projects/${projectId}/documents`).then((r) => setDocs(r.data)), [projectId]);
  useEffect(() => { load(); api.get(`/projects/${projectId}/report-options`).then((r) => setOpts(r.data)); }, [projectId, load]);
  useEffect(() => { api.get(`/projects/${projectId}/positions`).then((r) => setOpts((o) => ({ ...(o || {}), positions: r.data }))); }, [projectId]);

  const links = link.type === "project" || !link.id ? { project_id: projectId } : { [`${link.type}_id`]: link.id };
  const del = async (id) => { await api.delete(`/files/${id}`); load(); };

  return (
    <div className="space-y-4" data-testid="documents-panel">
      <h3 className="font-head font-bold flex items-center gap-2"><FolderOpen className="h-5 w-5 text-primary" />Dokumente <span className="text-sm text-slate-400 font-normal">({docs.length})</span></h3>
      <Card className="p-4 space-y-3">
        <DocumentLinker link={link} setLink={setLink} opts={opts} />
        <MediaUploader links={links} extraFields={{ category: "document" }} onUploaded={load} documentsOnly />
        <p className="text-xs text-slate-400">Unterstützt: PDF, Word, Excel, Bilder, Pläne (DWG/DXF)</p>
      </Card>
      {docs.length === 0 && <Card className="p-8 text-center text-slate-400">Noch keine Dokumente.</Card>}
      <div className="space-y-2">
        {docs.map((f) => <DocumentRow key={f.id} f={f} onDelete={del} />)}
      </div>
    </div>
  );
}
